/**
 * Capsule collision queries. Allocation-free; every function takes scratch from the caller.
 * Three-free.
 *
 * Fighting-game hit detection is a discrete-frame question ("do these volumes overlap on this
 * tick?"), but fast limbs and dashes can tunnel at 120 Hz, so the swept variants exist for
 * volumes whose owner moved more than its own radius in one step (ARCHITECTURE.md §4).
 */

import { closestPointOnSegment, vec3, clamp01, EPSILON } from '../core/index.js';

/** Shared scratch. Safe because every query completes before the next begins (single-threaded). */
const _pa = vec3();
const _pb = vec3();
const _qa = vec3();
const _qb = vec3();
const _ca = vec3();
const _cb = vec3();

/**
 * Squared distance between two capsule segments, plus the closest points.
 * Uses four alternating projections, which converges to within a few micrometres for the
 * short, roughly-parallel-or-crossing segments limbs actually produce.
 *
 * @returns {number} squared distance between the segments (not accounting for radii)
 */
export function segmentDistanceSq(a, b, outA, outB) {
  _pa.x = a.ax; _pa.y = a.ay; _pa.z = a.az;
  _pb.x = a.bx; _pb.y = a.by; _pb.z = a.bz;
  _qa.x = b.ax; _qa.y = b.ay; _qa.z = b.az;
  _qb.x = b.bx; _qb.y = b.by; _qb.z = b.bz;

  // Seed with the midpoint of B, then alternate projections.
  _cb.x = (_qa.x + _qb.x) * 0.5;
  _cb.y = (_qa.y + _qb.y) * 0.5;
  _cb.z = (_qa.z + _qb.z) * 0.5;

  for (let i = 0; i < 4; i++) {
    closestPointOnSegment(_ca, _pa, _pb, _cb);
    closestPointOnSegment(_cb, _qa, _qb, _ca);
  }

  if (outA) {
    outA.x = _ca.x; outA.y = _ca.y; outA.z = _ca.z;
  }
  if (outB) {
    outB.x = _cb.x; outB.y = _cb.y; outB.z = _cb.z;
  }
  const dx = _ca.x - _cb.x;
  const dy = _ca.y - _cb.y;
  const dz = _ca.z - _cb.z;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Do two capsules overlap? The fast path for hit detection.
 * @param {object} contact optional record filled with {x,y,z,nx,ny,nz,depth} on a hit
 */
export function capsulesOverlap(a, b, contact = null) {
  const rSum = a.r + b.r;
  // Broad phase on the segment bounding boxes first — rejects the common non-overlap case
  // in a handful of comparisons.
  if (Math.min(a.ax, a.bx) - rSum > Math.max(b.ax, b.bx)) return false;
  if (Math.max(a.ax, a.bx) + rSum < Math.min(b.ax, b.bx)) return false;
  if (Math.min(a.ay, a.by) - rSum > Math.max(b.ay, b.by)) return false;
  if (Math.max(a.ay, a.by) + rSum < Math.min(b.ay, b.by)) return false;
  if (Math.min(a.az, a.bz) - rSum > Math.max(b.az, b.bz)) return false;
  if (Math.max(a.az, a.bz) + rSum < Math.min(b.az, b.bz)) return false;

  const dSq = segmentDistanceSq(a, b, _ca, _cb);
  if (dSq > rSum * rSum) return false;

  if (contact) {
    const d = Math.sqrt(dSq);
    // Contact point sits on the surface between the two closest points, biased toward the
    // struck volume so impact FX spawn on the victim rather than inside the attacker's fist.
    const t = d > EPSILON ? a.r / rSum : 0.5;
    contact.x = _ca.x + (_cb.x - _ca.x) * t;
    contact.y = _ca.y + (_cb.y - _ca.y) * t;
    contact.z = _ca.z + (_cb.z - _ca.z) * t;
    if (d > EPSILON) {
      contact.nx = (_cb.x - _ca.x) / d;
      contact.ny = (_cb.y - _ca.y) / d;
      contact.nz = (_cb.z - _ca.z) / d;
    } else {
      contact.nx = 0;
      contact.ny = 1;
      contact.nz = 0;
    }
    contact.depth = rSum - d;
  }
  return true;
}

/**
 * Swept overlap: treats capsule `a` as moving from its previous pose to its current pose and
 * samples along the path. Used for fast limbs and dash-through cases where a discrete test
 * would tunnel. `steps` of 3 is enough at 120 Hz for anything short of a projectile.
 *
 * @param {object} aPrev capsule at the previous tick
 * @param {object} aNow capsule at the current tick
 */
export function capsuleSweepOverlap(aPrev, aNow, b, contact = null, steps = 3) {
  // If the volume barely moved relative to its own radius, the discrete test is exact enough.
  const dx = aNow.ax - aPrev.ax;
  const dy = aNow.ay - aPrev.ay;
  const dz = aNow.az - aPrev.az;
  const moved = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (moved <= aNow.r * 0.9) return capsulesOverlap(aNow, b, contact);

  const n = Math.max(2, Math.min(8, Math.ceil(moved / (aNow.r * 0.9)) + 1, steps + 1));
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    _lerpCapsule(_sweep, aPrev, aNow, t);
    if (capsulesOverlap(_sweep, b, contact)) return true;
  }
  return false;
}

const _sweep = {
  ax: 0, ay: 0, az: 0, bx: 0, by: 0, bz: 0, r: 0,
  kind: '', owner: -1, active: true, level: '', priority: 0, joint: '',
};

function _lerpCapsule(out, a, b, t) {
  out.ax = a.ax + (b.ax - a.ax) * t;
  out.ay = a.ay + (b.ay - a.ay) * t;
  out.az = a.az + (b.az - a.az) * t;
  out.bx = a.bx + (b.bx - a.bx) * t;
  out.by = a.by + (b.by - a.by) * t;
  out.bz = a.bz + (b.bz - a.bz) * t;
  out.r = a.r + (b.r - a.r) * t;
  return out;
}

/** Sphere-sphere test, used for throw boxes. */
export function spheresOverlap(ax, ay, az, ar, bx, by, bz, br) {
  const dx = ax - bx;
  const dy = ay - by;
  const dz = az - bz;
  const rs = ar + br;
  return dx * dx + dy * dy + dz * dz <= rs * rs;
}

/**
 * Horizontal (XZ) separation of two vertical pushbox capsules.
 * Fighting games separate on the ground plane only — a fighter is never pushed upward by
 * another fighter's body, because that would break juggle heights.
 *
 * @returns {number} overlap depth in metres, 0 if separated
 */
export function pushboxOverlapXZ(a, b, outNormal) {
  // Vertical ranges must intersect for a push to occur (so a juggled fighter overhead
  // does not shove the fighter below them).
  const aLo = Math.min(a.ay, a.by) - a.r;
  const aHi = Math.max(a.ay, a.by) + a.r;
  const bLo = Math.min(b.ay, b.by) - b.r;
  const bHi = Math.max(b.ay, b.by) + b.r;
  if (aHi < bLo || bHi < aLo) return 0;

  const dx = b.ax - a.ax;
  const dz = b.az - a.az;
  const distSq = dx * dx + dz * dz;
  const rSum = a.r + b.r;
  if (distSq >= rSum * rSum) return 0;

  const dist = Math.sqrt(distSq);
  if (dist < EPSILON) {
    // Perfectly coincident: separate along the combat axis deterministically by index so the
    // result never depends on floating-point noise.
    outNormal.x = a.owner <= b.owner ? -1 : 1;
    outNormal.y = 0;
    outNormal.z = 0;
    return rSum;
  }
  outNormal.x = dx / dist;
  outNormal.y = 0;
  outNormal.z = dz / dist;
  return rSum - dist;
}

/**
 * Distance from a point to a capsule surface. Negative inside.
 * Used by the AI to reason about reach without constructing volumes.
 */
export function pointCapsuleDistance(px, py, pz, c) {
  _pa.x = c.ax; _pa.y = c.ay; _pa.z = c.az;
  _pb.x = c.bx; _pb.y = c.by; _pb.z = c.bz;
  _qa.x = px; _qa.y = py; _qa.z = pz;
  closestPointOnSegment(_ca, _pa, _pb, _qa);
  const dx = px - _ca.x;
  const dy = py - _ca.y;
  const dz = pz - _ca.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz) - c.r;
}

/**
 * Horizontal reach of a capsule ahead of a fighter's origin, along the facing axis.
 * This is the number the AI and `MOVE_LIST.md` call a move's "range".
 */
export function forwardReach(c, originX, facing) {
  const a = (c.ax - originX) * facing;
  const b = (c.bx - originX) * facing;
  return Math.max(a, b) + c.r;
}

export { clamp01 };
